import { z } from 'zod';
import { INTEGRATION_TYPES } from '../integrations.constants';
import {
  CONFIG_CHANGE_SUPPORT,
  resolveConfigChangeCapability,
} from './config-change-capabilities';
import { IpsSignatureIdSchema } from './ips-dialect-capabilities';
import { readAwsBucketName } from './prevention-aws-bucket-name';
import {
  CONFIG_AGENT_DETAILS_SCHEMAS,
  CONFIG_DETAILS_SCHEMAS,
  refineConfigDetailsForChange,
} from './prevention-config-details.schema';
import {
  collectWafVendorCapabilityViolations,
  resolveWafVendorSupportedTargets,
} from './waf-vendor-capabilities';
import {
  ConfigControlSpecSchema,
  IpsControlSpecSchema,
  NetworkControlSpecSchema,
  PREVENTION_CONTROL_TYPES,
  WafControlSpecSchema,
  WafMatchOperatorSchema,
  WafMatchTransformSchema,
  type CloudProvider,
  type PreventionProvider,
  type WafMatchTarget,
} from './prevention.schema';

export const IpsControlSpecToolSchema = IpsControlSpecSchema.extend({
  signatureId: IpsSignatureIdSchema,
});

export const IpsControlSpecAgentToolSchema = IpsControlSpecToolSchema.omit({
  direction: true,
  protocol: true,
  targetIp: true,
  cidr: true,
  sensorScope: true,
  ttlSeconds: true,
}).strict();

const NetworkControlSpecAgentToolSchema = NetworkControlSpecSchema.omit({
  direction: true,
  scopeType: true,
  scopeId: true,
  targetIp: true,
  targetPort: true,
  protocol: true,
  ttlSeconds: true,
}).strict();

export const AwsSecurityGroupControlSpecToolSchema =
  NetworkControlSpecAgentToolSchema;
export const GcpFirewallControlSpecToolSchema =
  NetworkControlSpecAgentToolSchema;
export const AzureNsgControlSpecToolSchema = NetworkControlSpecAgentToolSchema;

export const ConfigControlSpecAgentToolSchema = z
  .object({
    controlType: z.literal(PREVENTION_CONTROL_TYPES.CONFIG),
    change: z.enum([
      'enforceTls',
      'disablePublicAccess',
      'dnsRecord',
      'emailAuth',
    ]),
    details: z.record(z.string(), z.unknown()).optional(),
    rationale: z.string().optional(),
  })
  .strict()
  .superRefine((spec, ctx) => {
    refineConfigDetailsForChange({
      change: spec.change,
      details: spec.details,
      ctx,
      schemas: CONFIG_AGENT_DETAILS_SCHEMAS,
    });
  });

export const WafControlSpecAgentToolSchema = WafControlSpecSchema.omit({
  ttlSeconds: true,
}).strict();

const buildWafVendorMatchConditionSchema = (
  vendor: string | null | undefined,
) => {
  const supportedTargets = resolveWafVendorSupportedTargets(vendor);
  const vendorTargetSchema = z.enum(
    supportedTargets as [WafMatchTarget, ...WafMatchTarget[]],
  );

  return z.strictObject({
    target: vendorTargetSchema,
    key: z.string().min(1).optional(),
    operator: WafMatchOperatorSchema,
    value: z.string().min(1).optional(),
    transform: WafMatchTransformSchema.optional(),
  });
};

export const buildWafControlSpecAgentToolSchema = (
  vendor: string | null | undefined,
) => {
  const vendorMatchConditionSchema = buildWafVendorMatchConditionSchema(vendor);

  return WafControlSpecAgentToolSchema.extend({
    matchConditions: z.array(vendorMatchConditionSchema).min(1),
  }).strict();
};

export const buildWafControlSpecToolSchema = (
  vendor: string | null | undefined,
) =>
  buildWafControlSpecAgentToolSchema(vendor).superRefine((spec, ctx) => {
    const violations = collectWafVendorCapabilityViolations({ spec, vendor });

    violations.forEach((message) => {
      ctx.addIssue({
        code: 'custom',
        message,
        path: ['matchConditions'],
      });
    });
  });

export const buildConfigControlSpecAgentToolSchema = () =>
  ConfigControlSpecAgentToolSchema;

export const buildConfigControlSpecToolSchema = (
  cloudProvider: CloudProvider | null | undefined,
) =>
  ConfigControlSpecSchema.superRefine((spec, ctx) => {
    refineConfigDetailsForChange({
      change: spec.change,
      details: spec.details,
      ctx,
      schemas: CONFIG_DETAILS_SCHEMAS,
    });
    if (!cloudProvider) {
      return;
    }

    const capability = resolveConfigChangeCapability({
      cloudProvider,
      change: spec.change,
    });

    if (!capability) {
      ctx.addIssue({
        code: 'custom',
        message: `change "${spec.change}" is not available for cloud provider ${cloudProvider}`,
        path: ['change'],
      });

      return;
    }

    if (capability.support === CONFIG_CHANGE_SUPPORT.UNSUPPORTED) {
      ctx.addIssue({
        code: 'custom',
        message: `change "${spec.change}" is unsupported for ${cloudProvider}`,
        path: ['change'],
      });
    }

    if (
      cloudProvider === 'aws' &&
      spec.change === 'disablePublicAccess' &&
      capability.support === CONFIG_CHANGE_SUPPORT.SUPPORTED &&
      !readAwsBucketName({ target: spec.target, details: spec.details })
    ) {
      ctx.addIssue({
        code: 'custom',
        message:
          'disablePublicAccess requires details.bucketName or a bucket-shaped target',
        path: ['details', 'bucketName'],
      });
    }
  });

export type PreventionRuleToolSchemaContext = {
  provider: PreventionProvider;
  vendor?: string | null;
  cloudProvider?: CloudProvider | null;
};

export const resolvePreventionRuleAgentToolSchema = ({
  provider,
  vendor,
  cloudProvider: _cloudProvider,
}: PreventionRuleToolSchemaContext): z.ZodType => {
  switch (provider) {
    case INTEGRATION_TYPES.GENERIC_WAF:
      return buildWafControlSpecAgentToolSchema(vendor);
    case INTEGRATION_TYPES.AWS_SECURITY_GROUP:
      return AwsSecurityGroupControlSpecToolSchema;
    case INTEGRATION_TYPES.GCP_FIREWALL:
      return GcpFirewallControlSpecToolSchema;
    case INTEGRATION_TYPES.AZURE_NSG:
      return AzureNsgControlSpecToolSchema;
    case INTEGRATION_TYPES.GENERIC_CONFIG:
      return buildConfigControlSpecAgentToolSchema();
    case INTEGRATION_TYPES.GENERIC_IPS:
      return IpsControlSpecAgentToolSchema;
    default:
      throw new Error(
        `No prevention rule tool schema for provider: ${provider}`,
      );
  }
};

export const resolvePreventionRuleToolSchema = ({
  provider,
  vendor,
  cloudProvider,
}: PreventionRuleToolSchemaContext): z.ZodType => {
  switch (provider) {
    case INTEGRATION_TYPES.GENERIC_WAF:
      return buildWafControlSpecToolSchema(vendor);
    case INTEGRATION_TYPES.AWS_SECURITY_GROUP:
    case INTEGRATION_TYPES.GCP_FIREWALL:
    case INTEGRATION_TYPES.AZURE_NSG:
      return NetworkControlSpecSchema;
    case INTEGRATION_TYPES.GENERIC_CONFIG:
      return buildConfigControlSpecToolSchema(cloudProvider);
    case INTEGRATION_TYPES.GENERIC_IPS:
      return IpsControlSpecToolSchema;
    default:
      throw new Error(
        `No prevention rule tool schema for provider: ${provider}`,
      );
  }
};

export const buildPreventionRuleToolSchemaCacheKey = ({
  provider,
  vendor,
  cloudProvider,
}: PreventionRuleToolSchemaContext): string => {
  if (provider === INTEGRATION_TYPES.GENERIC_WAF) {
    return `${provider}:${vendor ?? 'generic'}`;
  }

  if (provider === INTEGRATION_TYPES.GENERIC_CONFIG) {
    return `${provider}:${cloudProvider ?? 'unknown'}`;
  }

  return provider;
};

export const readCloudProviderFromDeploymentContext = ({
  deploymentContext,
  enrichedTarget,
}: {
  deploymentContext?: {
    cloudProvider?: CloudProvider | null;
  } | null;
  enrichedTarget?: Record<string, unknown>;
}): CloudProvider | null => {
  if (deploymentContext?.cloudProvider) {
    return deploymentContext.cloudProvider;
  }

  const value = enrichedTarget?.cloudProvider;

  if (
    typeof value === 'string' &&
    (value === 'aws' || value === 'gcp' || value === 'azure')
  ) {
    return value;
  }

  return null;
};
